import { useQuery } from '@tanstack/react-query';
import {
  HttpError,
  type FlightAlertRow,
  type FlightAlertTravelerResponse,
  type TripView,
} from '@voll-intelligence/domain';
import { calendarDateFromUtcDay, formatTripRange, tripName } from '@voll-intelligence/trip-panel';
import { useState } from 'react';

import { queryKeys } from '@web/core/query';
import { flightAlertsRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import {
  Button,
  CustomerProfilePanel,
  Kv,
  KvSection,
  TripItineraryWithVoucher,
  useOrgContext,
} from '@web/ui/core';
import { fireAndForget, formatDdMm, formatHhMm } from '@web/ui/core/utils';

import {
  departureZone,
  liveDetails,
  noticeText,
  STRIKETHROUGH_THRESHOLD_MIN,
} from './alert_row_facts';

/** Provider status codes → copy. Anything else (or null) reads as "no status". */
const STATUS_KEYS = {
  S: 'alerts.status.S',
  A: 'alerts.status.A',
  L: 'alerts.status.L',
  C: 'alerts.status.C',
  D: 'alerts.status.D',
  R: 'alerts.status.R',
} as const;

function statusText(status: string | null): string {
  const key =
    status !== null && status in STATUS_KEYS
      ? STATUS_KEYS[status as keyof typeof STATUS_KEYS]
      : null;
  return key ? t(key) : t('alerts.status.unknown');
}

/** `dd/mm HH:MM` in the zone — the day rides along because the panel spans two days. */
function dayTime(utc: string | Date, zone: string): string {
  return `${formatDdMm(utc, { timeZone: zone })} ${formatHhMm(utc, { timeZone: zone })}`;
}

/** Scheduled, plus the estimate when it moved by ≥5 minutes (same rule as the row). */
function ScheduleLines({
  scheduledUtc,
  estimatedUtc,
  zone,
}: Readonly<{
  scheduledUtc: string;
  estimatedUtc: string | null;
  zone: string;
}>): React.JSX.Element {
  // The estimate worth showing: null when absent or within the noise threshold.
  const estimate =
    estimatedUtc !== null &&
    Math.abs(new Date(estimatedUtc).getTime() - new Date(scheduledUtc).getTime()) / 60_000 >=
      STRIKETHROUGH_THRESHOLD_MIN
      ? estimatedUtc
      : null;
  return (
    <span className="flex flex-col">
      <span className={estimate === null ? undefined : 'text-muted-foreground line-through'}>
        {t('alerts.details.scheduled', { when: dayTime(scheduledUtc, zone) })}
      </span>
      {estimate === null ? null : (
        <span>{t('alerts.details.estimated', { when: dayTime(estimate, zone) })}</span>
      )}
    </span>
  );
}

/**
 * The facts the table has no room for: voucher, both ends of the leg with the
 * calendar day, the airline's own status, gate/terminal, how fresh the live data
 * is, and the notice line. Departure instants render in the DEPARTURE airport's
 * zone, arrival instants in the ARRIVAL airport's (they differ on GRU → MIA);
 * each falls back to the operator's zone and the section title says which.
 */
function FlightFacts({ row }: Readonly<{ row: FlightAlertRow }>): React.JSX.Element {
  const dep = departureZone(row.departure.timeZone);
  const arr = departureZone(row.arrival?.timeZone ?? null);
  const zoneTitle = dep.isAirport
    ? t('alerts.details.zone', { zone: dep.zone })
    : t('alerts.details.localZone', { zone: dep.zone });
  const gate = liveDetails(row);
  return (
    <section aria-label={t('alerts.details.flightAria')} title={zoneTitle}>
      <KvSection icon="warning-octagon" label={t('alerts.details.flight')}>
        <Kv label={t('alerts.details.voucher')} mono>
          {row.flight.voucherCode}
        </Kv>
        <Kv label={t('alerts.details.status')}>{statusText(row.live.status)}</Kv>
        <Kv label={t('alerts.details.departure')}>
          <ScheduleLines
            scheduledUtc={row.departure.scheduledUtc}
            estimatedUtc={row.departure.estimatedUtc}
            zone={dep.zone}
          />
        </Kv>
        <Kv label={t('alerts.details.arrival')} empty={row.arrival === undefined}>
          {row.arrival ? (
            <ScheduleLines
              scheduledUtc={row.arrival.scheduledUtc}
              estimatedUtc={row.arrival.estimatedUtc}
              zone={arr.zone}
            />
          ) : (
            '—'
          )}
        </Kv>
        <Kv label={t('alerts.details.gate')} empty={gate === null}>
          {gate ?? '—'}
        </Kv>
        <Kv label={t('alerts.details.observedAt')} empty={row.live.observedAt === null}>
          {row.live.observedAt ? dayTime(row.live.observedAt, dep.zone) : '—'}
        </Kv>
        <Kv label={t('alerts.details.notice')} wide>
          {noticeText(row)}
        </Kv>
      </KvSection>
    </section>
  );
}

function isClientError(err: unknown): boolean {
  return err instanceof HttpError && err.status < 500;
}

/**
 * The traveler's trip holding the alerted voucher, drawn with the SAME itinerary
 * the card trip column and the traveler app use. Read-only (no compose, no
 * archive); a step opens the voucher in the shared drawer.
 */
function TripPanel({ alertKey }: Readonly<{ alertKey: string }>): React.JSX.Element {
  const { orgId } = useOrgContext();
  const query = useQuery({
    queryKey: queryKeys.alerts.trip(orgId, alertKey),
    queryFn: () => flightAlertsRepository.trip(orgId, alertKey),
    // Same retry contract as the list: one immediate extra attempt for a
    // transient failure, none for an answer that will not change (4xx).
    retry: (count, err) => !isClientError(err) && count < 1,
    retryDelay: 0,
    meta: { silent: true },
  });

  let body: React.ReactNode;
  if (query.isPending) {
    body = (
      <p role="status" className="m-0 text-meta text-muted-foreground">
        {t('alerts.trip.loading')}
      </p>
    );
  } else if (query.isError) {
    body = (
      <div role="alert" className="flex flex-col items-start gap-3">
        <p className="m-0 text-meta text-tone-red-fg">{t('alerts.trip.error')}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            fireAndForget(query.refetch());
          }}
        >
          {t('alerts.trip.retry')}
        </Button>
      </div>
    );
  } else if (query.data === null) {
    body = (
      <p role="status" className="m-0 text-meta text-muted-foreground">
        {t('alerts.trip.none')}
      </p>
    );
  } else {
    body = <TripReady trip={query.data} />;
  }
  return (
    <section
      aria-label={t('alerts.details.tripAria')}
      className="flex min-w-0 flex-col gap-3 rounded-lg border border-border-soft bg-surface px-4 py-3"
    >
      {body}
    </section>
  );
}

function TripReady({ trip }: Readonly<{ trip: TripView }>): React.JSX.Element {
  // Day-only dates at UTC midnight: converted so a negative zone does not print
  // the range a day early (same as the card trip column).
  const range = formatTripRange(
    calendarDateFromUtcDay(trip.startAt),
    calendarDateFromUtcDay(trip.endAt),
  );
  return (
    <>
      <header className="flex flex-col gap-0.5">
        <h3 className="m-0 text-base font-medium text-foreground">{tripName(trip)}</h3>
        {range ? <p className="m-0 text-meta text-muted-foreground">{range}</p> : null}
      </header>
      <TripItineraryWithVoucher trip={trip} className="min-w-0" />
    </>
  );
}

function isNotFound(err: unknown): boolean {
  return err instanceof HttpError && err.status === 404;
}

/**
 * The traveler's operational profile + company, fetched ONLY once the operator opens
 * the disclosure (`TravelerDisclosure`): the panel is for having the data at hand,
 * not for reading every traveler's file on each expand. Same `CustomerProfilePanel`
 * as the customer screen.
 */
function TravelerPanel({ alertKey }: Readonly<{ alertKey: string }>): React.JSX.Element {
  const { orgId } = useOrgContext();
  const query = useQuery({
    queryKey: queryKeys.alerts.traveler(orgId, alertKey),
    queryFn: () => flightAlertsRepository.traveler(orgId, alertKey),
    retry: (count, err) => !isClientError(err) && count < 1,
    retryDelay: 0,
    meta: { silent: true },
  });

  let body: React.ReactNode;
  if (query.isPending) {
    body = (
      <p role="status" className="m-0 text-meta text-muted-foreground">
        {t('alerts.traveler.loading')}
      </p>
    );
  } else if (query.isError && isNotFound(query.error)) {
    body = (
      <p role="status" className="m-0 text-meta text-muted-foreground">
        {t('alerts.traveler.notFound')}
      </p>
    );
  } else if (query.isError) {
    body = (
      <div role="alert" className="flex flex-col items-start gap-3">
        <p className="m-0 text-meta text-tone-red-fg">{t('alerts.traveler.error')}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            fireAndForget(query.refetch());
          }}
        >
          {t('alerts.traveler.retry')}
        </Button>
      </div>
    );
  } else {
    body = <TravelerReady data={query.data} />;
  }
  return (
    <section aria-label={t('alerts.traveler.toggle')} className="flex min-w-0 flex-col gap-3">
      {body}
    </section>
  );
}

function TravelerReady({
  data,
}: Readonly<{ data: FlightAlertTravelerResponse }>): React.JSX.Element {
  return (
    <>
      {data.company ? (
        <p className="m-0 text-meta text-muted-foreground">
          {t('alerts.traveler.company')}:{' '}
          <span className="text-foreground">{data.company.name}</span>
        </p>
      ) : null}
      <CustomerProfilePanel profile={data.profile} hotelLoyalty={data.hotelLoyalty} />
    </>
  );
}

/** "Dados do viajante" — collapsed by default; the panel (and its fetch) mounts on open. */
function TravelerDisclosure({ alertKey }: Readonly<{ alertKey: string }>): React.JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-3">
      <div>
        <Button
          variant="outline"
          size="sm"
          aria-expanded={open}
          onClick={() => {
            setOpen((v) => !v);
          }}
        >
          {t('alerts.traveler.toggle')}
        </Button>
      </div>
      {open ? <TravelerPanel alertKey={alertKey} /> : null}
    </div>
  );
}

/**
 * The expanded row under an alert: flight facts on the left, the traveler's trip
 * on the right. Its own `<tr>` so the table keeps one column grid; `colSpan`
 * comes from the page, which owns the column list.
 */
export function AlertDetailsRow({
  row,
  colSpan,
}: Readonly<{ row: FlightAlertRow; colSpan: number }>): React.JSX.Element {
  return (
    <tr className="border-b border-border-soft bg-paper last:border-b-0">
      <td colSpan={colSpan} className="px-4 py-4">
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
            <FlightFacts row={row} />
            <TripPanel alertKey={row.alertKey} />
          </div>
          <TravelerDisclosure alertKey={row.alertKey} />
        </div>
      </td>
    </tr>
  );
}
